import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icons';

// A horizontal, button-driven carousel (never auto-advancing). Children slide left/right; the
// prev/next controls are disabled at each end and hidden entirely when everything already fits.
// Reusable — pair it with any fixed/min-width cards (each child should be `shrink-0`).
export function Carousel({
  children,
  ariaLabel,
  className = '',
}: {
  children: ReactNode;
  ariaLabel?: string;
  className?: string;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  // Recompute which arrows are usable from the current scroll offset. The 4px slop absorbs
  // sub-pixel rounding so the end state resolves cleanly to disabled.
  const sync = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  // Track scroll + size: content width and viewport both change what's reachable (e.g. a filter
  // that shrinks the list, or a window resize), so a ResizeObserver keeps the arrows honest.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    sync();
    el.addEventListener('scroll', sync, { passive: true });
    // ResizeObserver is an enhancement (keeps arrows honest on layout changes) — guard it so the
    // component still works where it's absent (older engines, jsdom in tests).
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', sync);
      ro?.disconnect();
    };
  }, [sync]);

  // Page by ~90% of the visible width so a card near the edge isn't left half-clipped.
  function page(dir: 1 | -1) {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.9), behavior: 'smooth' });
  }

  return (
    <div className={'relative ' + className}>
      <div
        ref={scrollerRef}
        role="group"
        aria-label={ariaLabel}
        // Layout-agnostic scroller: the caller supplies the inner track (a single row, or a 2-row
        // grid). `scroll-hide` (see index.css) hides the native scrollbar so the arrows are the
        // only affordance.
        className="scroll-hide overflow-x-auto scroll-smooth pb-1"
      >
        {children}
      </div>

      {canPrev && <ArrowButton dir="prev" onClick={() => page(-1)} />}
      {canNext && <ArrowButton dir="next" onClick={() => page(1)} />}
    </div>
  );
}

function ArrowButton({ dir, onClick }: { dir: 'prev' | 'next'; onClick: () => void }) {
  const isPrev = dir === 'prev';
  const Glyph = isPrev ? Icon.chevronLeft : Icon.chevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={isPrev ? 'Previous' : 'Next'}
      className={
        'absolute top-1/2 z-10 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full border ' +
        'border-line-2 bg-surface text-ink shadow-pop transition-colors hover:bg-surface-2 ' +
        (isPrev ? '-left-3' : '-right-3')
      }
    >
      <Glyph className="h-4 w-4" />
    </button>
  );
}
